import React, {useContext, useEffect, useState} from 'react';
import {usePowerSyncData} from './PowerSyncDataProvider';
import {PERMISSIONS, STORAGE_KEYS} from '../Constants/AppConstants';
import LocalStorageService from '../Services/LocalStorageService';
import {ILocalStorageService} from '../Services/Interfaces/ILocalStorageService';
import GeneralHelperService from '../Services/GeneralHelperService';
import {IGeneralHelperService} from '../Services/Interfaces/IGeneralHelperService';
import {User} from 'oidc-client-ts';
import useEHSAuth from '../Hooks/useEHSAuth';

type PermissionProviderProps = {
  children: React.ReactNode;
};

type PermissionContextValue = {
  hasPermission: (permission: string) => boolean;
};

const PermissionContext = React.createContext<PermissionContextValue>(
  {} as PermissionContextValue,
);

//Mongo Permissions table has list of permissions with an array of roles that has access to that permission
const PermissionProvider: React.FunctionComponent<PermissionProviderProps> = ({
  children,
}): React.ReactElement => {
  const {permissions} = usePowerSyncData();
  const {getUser} = useEHSAuth();

  const storageService: ILocalStorageService = new LocalStorageService();
  const helperService: IGeneralHelperService = new GeneralHelperService();

  const hasPermission = (permission: string): boolean => {
    let oidcAuth = storageService.getOidc();

    if (!oidcAuth) return false;
    let oidcUser = User.fromStorageString(oidcAuth);
    let jwt = oidcUser.access_token;

    if (!jwt) return false;

    let jwtRoles = helperService.getRolesFromJwt(jwt);
    let permissionRoles = permissions.find(x => x.name == permission)?.roles;
    if (!permissionRoles) return false;

    for (let i = 0; i < permissionRoles?.length; i++) {
      if (jwtRoles.includes(permissionRoles[i])) return true;
    }

    return false;
  };

  const value = {
    hasPermission: hasPermission,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

const usePermission = () => {
  const permissionContext = useContext(PermissionContext);

  if (permissionContext === null)
    throw new Error('usePermission() called outside of a PermissionProvider?');

  return permissionContext;
};

export {PermissionProvider, PermissionContext, usePermission};
